import moment from "moment";
import { useState, useEffect } from "react";
import {
  fetchSalaries,
  calculateAllEmployeeSalary,
} from "app/API/SalaryCalculationAPI";
import { Modal, Form, Button } from "react-bootstrap";
import swal from "sweetalert2";
import { Dropdown, Menu, Space } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { fetchEmployeeLoanBalances } from "app/API/LoanAPI";

const MySalary = ({
  employeeDetails,
  triggerSalaryRefetch,
  fromCalculationList,
}) => {
  const [salaryList, setSalaryList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [month, setMonth] = useState(moment().month() + 1);
  const [year, setYear] = useState(moment().year());
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [salaryMonth, setSalaryMonth] = useState("");
  const [salaryYear, setSalaryYear] = useState("");
  const [employeeLoanBalances, setEmployeeLoanBalances] = useState({});
  const navigate = useNavigate();

  const StoredToken = localStorage.getItem("token");

  const handleMonthChange = (selectedMonth) => {
    setMonth(selectedMonth);
    fetchData(currentPage, itemsPerPage, selectedMonth, year);
  };


  const fetchLoanBalances = async () => {
    try {
      const balances = await fetchEmployeeLoanBalances();
      const balancesMap = balances.reduce((acc, balance) => {
        acc[balance.employee_id] = balance.total_balance;
        return acc;
      }, {});
      setEmployeeLoanBalances(balancesMap);
    } catch (error) {
      console.error("Error fetching employee loan balances:", error);
    }
  };

  const handleYearChange = (selectedYear) => {
    setYear(selectedYear);
    fetchData(currentPage, itemsPerPage, month, selectedYear);
  };

  const handleResetFilter = () => {
    const currentMonth = moment().month() + 1;
    const currentYear = moment().year();
    setMonth(currentMonth);
    setYear(currentYear);
    fetchData(currentPage, itemsPerPage, currentMonth, currentYear);
  };

  const fetchData = async (page, limit, month, year) => {
    try {
      const response = await fetchSalaries(
        employeeDetails?.id,
        month,
        year,
        page,
        limit,
        StoredToken
      );
      const salaryData = response.salary_calculations.map((record, index) => ({
        ...record,
        index: (page - 1) * limit + index + 1,
      }));
      setSalaryList(salaryData);
      setTotalItems(response.total_count);
    } catch (error) {
      console.error("Error fetching salary data:", error);
    }
  };

  useEffect(() => {
    fetchData(currentPage, itemsPerPage, month, year);
    fetchLoanBalances();
  }, [currentPage, itemsPerPage, month, year, triggerSalaryRefetch]);

  const toggleFilterDropdown = () => {
    setFilterDropdownOpen(!filterDropdownOpen);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const getVisiblePages = () => {
    const pageRange = 5;
    const start = Math.max(1, currentPage - Math.floor(pageRange / 2));
    const end = Math.min(totalPages, start + pageRange - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  // salary calculate logic

  const handleSalaryCalculate = () => {
    setShowSalaryModal(true);
  };

  const handleSalaryModalClose = () => {
    setShowSalaryModal(false);
    setSalaryMonth("");
    setSalaryYear("");
  };

  const handleSalarySubmit = async () => {
    const month = salaryMonth; // Assuming salaryMonth is already set
    const year = salaryYear; // Assuming salaryYear is already set

    try {
      // Call the salary calculation API
      const response = await calculateAllEmployeeSalary(
        month,
        year,
        StoredToken
      );

      if (response && !response.error) {
        swal.fire({
          icon: "success",
          title: "Success!",
          text: response.msg || "Salary of active employees calculated successfully.",
        });

        setSalaryMonth("");
        setSalaryYear("");

        // Refresh the salary calculation list after successful calculation
        fetchData(currentPage, itemsPerPage, month, year);
      } else {
        // Show error message
        let errorTitle = "Calculation Failed";
        let errorText = response.msg || "Failed to calculate salary.";
        let errorIcon = "error";

        // Handle specific error types from backend
        if (response.errorType) {
          switch (response.errorType) {
            case 'EMPLOYEE_NOT_FOUND':
              errorTitle = "Employee Not Found";
              errorText = "The selected employee could not be found.";
              errorIcon = "warning";
              break;
            case 'EMPLOYEE_INACTIVE':
              errorTitle = "Employee Inactive";
              errorText = "Cannot calculate salary for inactive employees.";
              errorIcon = "info";
              break;
            case 'SALARY_DATA_MISSING':
              errorTitle = "Salary Data Missing";
              errorText = "Employee salary configuration is missing. Please set up salary details first.";
              errorIcon = "warning";
              break;
            case 'NEGATIVE_SALARY':
              errorTitle = "Negative Salary";
              errorText = "Calculated salary is negative. Please review deductions and allowances.";
              errorIcon = "warning";
              break;
            case 'VALIDATION_ERROR':
              errorTitle = "Invalid Input";
              errorText = response.msg || "Please check the month and year values.";
              errorIcon = "warning";
              break;
            default:
              errorTitle = "Calculation Error";
              errorText = response.msg || "An error occurred during salary calculation.";
          }
        }

        swal.fire({
          icon: errorIcon,
          title: errorTitle,
          text: errorText,
        });
      }
    } catch (error) {
      console.error("Bulk salary calculation error:", error);
      
      // Handle specific error types
      let errorTitle = "Error";
      let errorText = "An error occurred while processing the salary calculation.";
      let errorIcon = "error";
      
      if (error.response?.data?.err) {
        const errorMessage = error.response.data.err;
        
        if (errorMessage.includes("No active employees found")) {
          errorTitle = "No Active Employees";
          errorText = "No active employees found for salary calculation.";
          errorIcon = "info";
        } else if (errorMessage.includes("negative")) {
          errorTitle = "Calculation Issues";
          errorText = "Some employees have negative salary calculations. Please review individual employee deductions.";
          errorIcon = "warning";
        } else {
          errorText = errorMessage;
        }
      } else if (error.message) {
        errorText = error.message;
      }
      
      swal.fire({
        icon: errorIcon,
        title: errorTitle,
        text: errorText,
        confirmButtonText: "OK"
      });
    } finally {
      // Close the modal after handling the process
      handleSalaryModalClose();
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: 11 },
    (_, index) => currentYear - 5 + index
  );

  const menu = (record) => (
    <Menu>
      <Menu.Item
        key="1"
        onClick={() => navigate("/loan/list", { state: { employee_id: record.employee_id } })}
      >
        Adjust loan
      </Menu.Item>
    </Menu>
  );
  

  return (
    <div>
      <div className="d-flex justify-content-end align-items-center mb-3 gap-3">
        {fromCalculationList && (
          <Button
            variant="primary"
            onClick={handleSalaryCalculate}
            className="d-flex align-items-center justify-content-center ms-2"
            style={{ height: "38px", width: "150px" }}
          >
            Calculate Salary
          </Button>
        )}

        <div className="dropdown">
          <button
            type="button"
            className="btn btn-primary dropdown-toggle"
            onClick={toggleFilterDropdown}
            style={{
              backgroundColor: "#BBC5EA",
              borderColor: "#BBC5EA",
              color: "#000000",
              height: "39px",
              width: "82px",
            }}
          >
            Filter&nbsp;
            <i className="fa fa-filter" aria-hidden="true"></i>
          </button>

          {filterDropdownOpen && (
            <div
              className="dropdown-menu"
              style={{
                display: "block",
                position: "absolute",
                right: 0,
                left: "auto",
                width: "320px",
                padding: "20px",
                borderRadius: "10px",
                border: "2px solid #DEE2E6",
              }}
            >
              <div style={{ marginBottom: "15px" }}>
                <label>Month:</label>
                <select
                  className="form-select"
                  onChange={(e) =>
                    handleMonthChange(parseInt(e.target.value, 10))
                  }
                  value={month}
                >
                  {moment.months().map((monthName, index) => (
                    <option key={index} value={index + 1}>
                      {monthName}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label>Year:</label>
                <select
                  className="form-select"
                  onChange={(e) =>
                    handleYearChange(parseInt(e.target.value, 10))
                  }
                  value={year}
                >
                  {Array.from(
                    { length: 10 },
                    (_, i) => moment().year() - i
                  ).map((yearOption) => (
                    <option key={yearOption} value={yearOption}>
                      {yearOption}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginTop: "15px" }}>
                <button
                  type="button"
                  className="btn"
                  style={{
                    color: "#0075FF",
                    borderColor: "#0075FF",
                    fontSize: "15px",
                  }}
                  onClick={handleResetFilter}
                >
                  Reset Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className="table-responsive"
        style={{ overflowX: "auto", width: "100%" }}
      >
        <table className="table common-table salary-calculation-table" style={{ minWidth: "1500px" }}>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Employee Name</th>
              <th>Month</th>
              <th>Year</th>
              <th>Att. Days</th>
              <th>Approved Leaves</th>
              <th>Working Days</th>
              <th>Holiday's</th>
              <th>Overtime Hours</th>
              <th>Pf</th>
              <th>Esi</th>
              <th>Loan Due</th>
              <th>Loan Deduction</th>

              <th>Calculated</th>
              <th>Final Salary</th>
              <th>Actions</th>

              {/* <th>Status</th> */}
            </tr>
          </thead>
          <tbody>
            {salaryList.length > 0 ? (
              salaryList.map((item) => (
                <tr key={item.id}>
                  <td>{item.index}</td>
                  <td>
                    {item?.employee?.first_name} {item?.employee?.last_name}
                  </td>

                  <td>
                    {moment()
                      .month(item.month - 1)
                      .format("MMMM")}
                  </td>
                  <td>{item.year}</td>
                  <td>{item.attendance_days}</td>
                  <td>{item.approved_leaves}</td>
                  <td>{item.working_days}</td>
                  <td>{item.monthly_holidays}</td>
                  <td>{item?.overtime_hours}</td>
                  <td>{item?.pf_deducted ? "Yes" : "No"}</td>
                  <td>{item?.esi_deducted ? "Yes" : "No"}</td>
                  <td>{employeeLoanBalances[item.employee_id] || 0}</td>
                  <td>{item?.loan_deduction}</td>
                  <td>{item?.salary_processed?.toFixed(2)}</td>
                  <td>{item?.final_salary}</td>
                  {/* <td>{item.status}</td> */}
                  <td>
                    <Space size="middle">
                      <Dropdown overlay={menu(item)}>
                        <a
                          className="ant-dropdown-link"
                          onClick={(e) => e.preventDefault()}
                        >
                          Options <DownOutlined />
                        </a>
                      </Dropdown>
                    </Space>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="16">No salary records available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="items-per-page"></div>

        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            First
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>

          {getVisiblePages().map((page) => (
            <button
              key={page}
              className={currentPage === page ? "active" : ""}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            Last
          </button>
        </div>
      </div>

      <Modal show={showSalaryModal} onHide={handleSalaryModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Calculate Salary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="salaryMonth">
              <Form.Label>Month</Form.Label>
              <Form.Control
                as="select"
                value={salaryMonth}
                onChange={(e) => setSalaryMonth(e.target.value)}
                aria-label="Select month for salary calculation"
              >
                <option value="">Select Month</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <option key={month} value={month}>
                    {new Date(0, month - 1).toLocaleString("default", {
                      month: "long",
                    })}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="salaryYear" className="mt-3">
              <Form.Label>Year</Form.Label>
              <Form.Control
                as="select"
                value={salaryYear}
                onChange={(e) => setSalaryYear(e.target.value)}
                aria-label="Select year for salary calculation"
              >
                <option value="">Select Year</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleSalaryModalClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSalarySubmit}
            disabled={!salaryMonth || !salaryYear}
          >
            Calculate
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MySalary;
